import React, { useState, useEffect, useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquare,
  faCheckSquare,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

import {
  createCommaSeparatedListFromArray,
  copyPreviousYearData,
  naturalSortCompare,
} from "../js/Helpers";

import "../css/switch_years.css";
import BasicTable from "./hsms/common/BasicTable";
import Collapsible from "./hsms/common/Collapsible";
import ConstraintsTable from "./hsms/common/ConstraintsTable";
import { USER_ROLES_MAP } from "../js/Constants";
import { useNewYearModalContext } from "./shared/SharedStateContext";

export default function NewYearModal({
  selectedScheduleYearID,
  schoolInfo,
  scheduleInfo,
  closeModal,
  callBack = () => {},
  scheduleStructureData,
  departments,
  teachers,
  courses,
  classrooms,
  sections,
  users,
  userInfo,
}) {
  ////////////////////////////////
  ///// SETTINGS/CONSTRAINTS /////
  ////////////////////////////////
  const data_settings =
    schoolInfo.type === "2"
      ? [
          { value: "teachers", display: "Teachers" },
          { value: "courses", display: "Courses" },
          { value: "classrooms", display: "Classrooms" },
          { value: "schedule_structure", display: "Periods" },
          { value: "blocks", display: "Blocks" },
        ]
      : [
          { value: "courses", display: "Courses" },
          { value: "teachers", display: "Teachers" },
          { value: "classrooms", display: "Classrooms" },
          { value: "departments", display: "Departments" },
          { value: "sections", display: "Sections" },
          { value: "section_placements", display: "Section Placements", prereqs: ["schedule_structure", "sections"], hideTab: true },
          { value: "schedule_structure", display: "Schedule Structure", hideTab: true },
          { value: "users", display: "Users" },
          // {value:'teacher_departments', display:'Teacher Departments', prereqs:['teachers', 'departments']},
          // {value:'course_departments', display:'Course Departments', prereqs:['courses', 'departments']},
          // {value:'classroom_departments', display:'Classroom Departments', prereqs:['classrooms', 'departments']},
          // {value:'department_supervisors', display:'Department Supervisors', prereqs:['departments']},
          // {value:'sections', display:'Sections'},
          // {value:'section_labels', display:'Section Labels', explanation:'This would copy over any section labels you created. This could be anything from a "Bilingual" label to a "Team" label.', prereqs:['sections']}
        ];

  const data_constraints =
    schoolInfo.type === "2"
      ? [
          {
            value: "teacher_availability",
            display: "Teacher Availability",
            explanation:
              "This would copy over any rules about what periods teachers can/cannot teach.",
            prereqs: ["teachers", "blocks", "schedule_structure"],
          },
          {
            value: "course_availability",
            display: "Course Availability",
            explanation:
              "This would copy over any rules about what periods courses can/cannot run.",
            prereqs: ["courses", "blocks", "schedule_structure"],
          },
          {
            value: "course_lengths",
            display: "Course Lengths",
            explanation:
              "This would copy over any settings about course length (semester vs full year), type of course (lunch, special, or pullout), and number of times a special/pullout is needed",
            prereqs: ["courses"],
          },
          {
            value: "homeroom_specials",
            display: "Homeroom Specials",
            explanation:
              "This would copy over any info about which homerooms need certain specials",
            prereqs: ["courses", "teachers"],
          },
          {
            value: "course_teachers",
            display: "Course Teachers",
            explanation:
              "This would copy over any settings about what courses teachers can teach.",
            prereqs: ["courses", "teachers"],
          },
          {
            value: "course_classrooms",
            display: "Course Classrooms",
            explanation:
              "This would copy over any rules about whether courses need to be in a specific classroom (like Music needs to be in a Music room).",
            prereqs: ["courses", "classrooms"],
          },
          {
            value: "teacher_classrooms",
            display: "Teacher Classrooms",
            explanation:
              "This would copy over any rules about teachers' classrooms.",
            prereqs: ["teachers", "classrooms"],
          },
        ]
      : [
          {
            value: "teacher_availability",
            display: "Teacher Availability",
            explanation:
              "This would copy over any rules about what periods teachers can/cannot teach.",
            prereqs: ["teachers", "schedule_structure"],
          },
          {
            value: "course_availability",
            display: "Course Availability",
            explanation:
              "This would copy over any rules about what periods courses can/cannot run.",
            prereqs: ["courses", "schedule_structure"],
          },
          {
            value: "course_caps",
            display: "Course Caps",
            explanation:
              "This would copy over any rules about max number of sections and max number of students per course.",
            prereqs: ["courses"],
          },
          {
            value: "course_lengths",
            display: "Course Lengths",
            explanation:
              "This would copy over any settings about course length (semester vs full year), course type (elective vs non-elective), and course span (1 period, 2 periods, etc).",
            prereqs: ["courses"],
          },
          {
            value: "course_flags",
            display: "Course Flags",
            explanation:
              "This would copy over if a course is flagged as a lunch, inclusion, lab, or virtual course.",
            prereqs: ["courses"],
          },
          {
            value: "course_teachers",
            display: "Course Teachers",
            explanation:
              "This would copy over any settings about what courses teachers can teach.",
            prereqs: ["courses", "teachers"],
          },
          {
            value: "classroom_settings",
            display: "Classroom Settings",
            explanation:
              "This would copy over any settings about classroom types (Chem lab, Media room, etc) and max classroom capacities.",
            prereqs: ["classrooms"],
          },
          {
            value: "course_classrooms",
            display: "Course Classrooms",
            explanation:
              "This would copy over any rules about whether courses need to be in a specific classroom (like Chem needs to be in a Chem lab).",
            prereqs: ["courses", "classrooms"],
          },
          {
            value: "teacher_classrooms",
            display: "Teacher Classrooms",
            explanation:
              "This would copy over any rules about teachers' preferred classrooms.",
            prereqs: ["teachers", "classrooms"],
          },
        ];

  /////////////////
  ///// STATE /////
  /////////////////
  const [topicDataLoading, setTopicDataLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(true);
  const { currentScreen, setCurrentScreen, currentDataTab, setCurrentDataTab, selectedDataCategories, setSelectedDataCategories, categoryData, setCategoryData, categoryDataSelection, setCategoryDataSelection, checkedConstraints, setCheckedConstraints, constraints, setConstraints, disabledCategoryDataIds, setDisabledCategoryDataIds } = useNewYearModalContext();

  const handleCheckConstraint = (id) => {
    toggleSettings(id);
    setCheckedConstraints((prev) => {
      if (prev.includes(id)) {
        return prev.filter((constraintId) => constraintId !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  
  const [openConstraint, setOpenConstraint] = useState(null);
  const handleConstraintToggle = (id) => {
    setOpenConstraint((prev) => (prev === id ? null : id));
  };

  const [settings, setSettings] = useState(null);
  const [
    ,
    setSchoolHasOnlyOneScheduleVersionID,
  ] = useState(false);
  const [constraintExplanationOpen, setConstraintExplanationOpen] = useState(
    []
  );
  const [errorMessage] = useState(null);

  /////////////////////
  ///// FUNCTIONS /////
  /////////////////////
  const handleCloseModal = () => {
    closeModal();
  };

  const handleMoveToPrevPage = () => {
    if (currentScreen === 2) {
      const currentSelectedTabs = selectedDataCategories.filter(tab => data_settings.find(setting => setting.value === tab)?.hideTab !== true);
      // if is the first tab, then go to the previous screen
      if (currentDataTab === currentSelectedTabs[0]) {
        setCurrentScreen(1);
        return;
      } else {
        // if not, then go to the previous tab
        const currentDataTabIndex = currentSelectedTabs.findIndex(
          (category) => category === currentDataTab
        );
        const prevDataTab = currentSelectedTabs[currentDataTabIndex - 1];
        handleDataTabChange(prevDataTab);
        return;
      }
    } else if (currentScreen === 3) {
      if (selectedDataCategories.length > 0) {
        setCurrentScreen(2);
      } else {
        setCurrentScreen(1);
      }
      return;
    }
    setCurrentScreen(currentScreen - 1);
  };

  const handleMoveToNextPage = () => {
    // Step 1 - Select data topics to copy
    if (currentScreen === 1) {
      const selectedDataCategories = [];
      if (settings.courses) {
        selectedDataCategories.push("courses");
      }
      if (settings.teachers) {
        selectedDataCategories.push("teachers");
      }
      if (settings.classrooms) {
        selectedDataCategories.push("classrooms");
      }
      if (settings.departments) {
        selectedDataCategories.push("departments");
      }
      if (settings.sections) {
        selectedDataCategories.push("sections");
      }
      if (settings.section_placements) {
        selectedDataCategories.push("section_placements");
      }
      if (settings.users) {
        selectedDataCategories.push("users");
      }
      if (selectedDataCategories.length > 0) {
        setSelectedDataCategories(selectedDataCategories);
        const firstTopic = selectedDataCategories[0];
        handleDataTabChange(firstTopic);
        setCurrentScreen(2);
      } else {
        // if empty selected topics, move to step 3
        setCurrentScreen(3);
      }
      return;
    } else if (currentScreen === 2) {
      // if is the last tab, then go to the next screen
      const currentSelectedTabs = selectedDataCategories.filter(tab => data_settings.find(setting => setting.value === tab)?.hideTab !== true);

      if (
        currentDataTab === currentSelectedTabs[currentSelectedTabs.length - 1]
      ) {
        const mappedConstraints = mapSchedulingConstraints(data_constraints, settings);
        setConstraints(mappedConstraints);
        setCheckedConstraints(mappedConstraints.filter(constraint => constraint.disabled === false).map(constraint => constraint.id));
        setCurrentScreen(3);
        return;
      } else {
        // if not, then go to the next tab
        const currentDataTabIndex = currentSelectedTabs.findIndex(
          (category) => category === currentDataTab
        );
        const nextDataTopic = currentSelectedTabs[currentDataTabIndex + 1];
        handleDataTabChange(nextDataTopic);
        return;
      }
    } else if (currentScreen === 3) {
      setCurrentScreen(4);
      handleCopyData();
      return;
    }
    setCurrentScreen(currentScreen + 1);
  };

  const handleDataTabChange = (category) => {
    loadTopicData(category);
    setCurrentDataTab(category);
  };

  const loadTopicData = async (category) => {
    setTopicDataLoading(true);

    try {
      switch (category) {
        case "courses":
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: courses,
          }));
          setTopicDataLoading(false);
          break;
        case "teachers":
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: teachers,
          }));
          setTopicDataLoading(false);
          break;
        case "classrooms":
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: classrooms,
          }));
          setTopicDataLoading(false);
          break;
        case "departments":
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: departments,
          }));
          setTopicDataLoading(false);
          break;
        case "sections":
          const selectedCourses = categoryDataSelection["courses"];
          const filteredSections = sections.filter((section) => {
            return !selectedCourses || selectedCourses.includes(section.course_id);
          });
          handleTopicDataSelectionChange(category, filteredSections.map(section => section.section_id));
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: filteredSections,
          }));
          setTopicDataLoading(false);
          break;
        case "users":
          setCategoryData((prevCategoryData) => ({
            ...prevCategoryData,
            [category]: users,
          }));
          if (userInfo && userInfo.user_id) {
            setDisabledCategoryDataIds((prevDisabledIds) => ({
              ...prevDisabledIds,
              [category]: [userInfo.user_id],
          }));
          }
          setTopicDataLoading(false);
          break;
        default:
          break;
      }
    } catch (error) {
      setTopicDataLoading(false);
    }
  };

  const mapTopicHeaders = (category) => {
    switch (category) {
      case "courses":
        return ["Course Code", "Course Name"];
      case "teachers":
        return ["Teacher Code", "Last Name", "First Name"];
      case "classrooms":
        return ["Classroom Name"];
      case "departments":
        return ["Department Name"];
      case "sections":
        return ["Course Code", "Course", "Section #", "Teacher"];
      case "users":
        return ["User Name", "Email", "Type"];
      default:
        break;
    }
  };

  const mapTopicRows = (category, categoryData) => {
    switch (category) {
      case "courses":
        return categoryData.map((course) => {
          return {
            id: course.course_id,
            course_code: course.course_code,
            course_name: course.name,
          };
        }).sort((a, b) => a.course_code.localeCompare(b.course_code));
      case "teachers":
        return categoryData.map((teacher) => {
          return {
            id: teacher.teacher_id,
            teacher_code: teacher.teacher_code,
            last_name: teacher.name,
            first_name: teacher.first_name,
          };
        });
      case "classrooms":
        return categoryData.map((classroom) => {
          return {
            id: classroom.classroom_id,
            classroom_name: classroom.classroom_name,
          };
        });
      case "departments":
        return categoryData.map((department) => {
          return {
            id: department.department_id,
            department_name: department.department,
          };
        });
      case "sections":
        return categoryData
          .map((section) => {
            const course = courses.find(
              (course) => course.course_id === section.course_id
            );
            const teacher = teachers.find(
              (teacher) => teacher.teacher_id === section.teacher_id
            );
            const selectedTeachers = categoryDataSelection["teachers"];
            const teacherName = teacher
              && (!selectedTeachers || selectedTeachers.includes(section.teacher_id))
              ? `${teacher.name}, ${teacher.first_name}`
              : "--";
            return {
              id: section.section_id,
              course_code:
                course && course.course_code ? course.course_code : "--",
              course: course && course.name ? course.name : "--",
              section_number: section.section_number || "--",
              teacher: teacherName,
            };
          })
          .sort((a, b) => {
            // 1st sort by course_code
            if (a.course_code !== b.course_code) {
              return a.course_code.localeCompare(b.course_code);
            }
            // 2nd by course name
            if (a.course !== b.course) {
              return a.course.localeCompare(b.course);
            }
            // 3rd by section number
            if (a.section_number !== b.section_number) {
              return naturalSortCompare(a.section_number, b.section_number);
            }
            // 4th by teacher name
            return a.teacher.localeCompare(b.teacher);
          });

      case "users":
        return categoryData.map((user) => {
          return {
            id: user.user_id,
            user_name: user.last_name + ", " + user.first_name,
            email: user.email,
            type: USER_ROLES_MAP[Number(user.role_id)],
          };
        });
      default:
        return [];
    }
  };

  const handleTopicDataSelectionChange = (category, selectedRows) => {
    setCategoryDataSelection((prevSelection) => ({
      ...prevSelection,
      [category]: selectedRows,
    }));
  };

  const toggleSettings = (setting_type, schedule_version_id = null) => {
    const current_setting = settings[setting_type];

    if (setting_type === "schedule_version_id") {
      const schedule_version_index =
        settings.schedule_version_ids_to_copy.findIndex(
          (temp_schedule_version_id) =>
            temp_schedule_version_id === schedule_version_id
        );

      if (schedule_version_index !== -1) {
        settings.schedule_version_ids_to_copy.splice(schedule_version_index, 1);
      } else {
        settings.schedule_version_ids_to_copy.push(schedule_version_id);
      }
    } else {
      settings[setting_type] = !current_setting;
    }

    // Check if prereqs are met for every setting
    // If not set as false in settings
    data_settings.forEach((data_setting) => {
      const data_setting_val = data_setting.value;
      const setting_prereqs = data_setting.prereqs;
      const all_prereqs_met = !setting_prereqs
        ? true
        : setting_prereqs.reduce((result, prereq) => {
            if (!settings[prereq]) result = false;
            return result;
          }, true);

      if (!all_prereqs_met) settings[data_setting_val] = false;
    });

    // Check if prereqs are met for every constraint
    // If not set as false in settings
    data_constraints.forEach((data_constraint) => {
      const data_constraint_val = data_constraint.value;
      const constraint_prereqs = data_constraint.prereqs;
      const all_prereqs_met = !constraint_prereqs
        ? true
        : constraint_prereqs.reduce((result, prereq) => {
            if (!settings[prereq]) result = false;
            return result;
          }, true);

      if (!all_prereqs_met) settings[data_constraint_val] = false;
    });

    setSettings({ ...settings });
  };

  const toggleConstraintExplanation = (constraint) => {
    const constraint_index = constraintExplanationOpen.findIndex(
      (temp_constraint) => temp_constraint === constraint
    );

    if (constraint_index !== -1) {
      constraintExplanationOpen.splice(constraint_index, 1);
    } else {
      constraintExplanationOpen.push(constraint);
    }

    setConstraintExplanationOpen([...constraintExplanationOpen]);
  };

  const mapSchedulingConstraints = (data_constraints = [], settings = []) => {
    const constraints = [];
    data_constraints.forEach((data_constraint) => {
      const id = data_constraint.value;
      const title = data_constraint.display;
      const constraintPrereqs = data_constraint.prereqs;
      const allPrereqsMet = !constraintPrereqs
        ? true
        : constraintPrereqs.reduce((result, prereq) => {
            if (settings && !settings[prereq]) result = false;
            return result;
          }, true);
      constraints.push({
        id,
        title,
        disabled: allPrereqsMet ? false : true,
        errorMessage: allPrereqsMet
          ? ""
          : `This option is disabled because you are not copying ${constraintPrereqs.join(
              ", "
            )} from last year. If you want to copy ${title}, please go back and make this change.`,
      });
    });
    return constraints;
  };

  const mapSelectedData = (selectedDataCategories = [], selectedData = []) => {
    const mappedData = {};
    for (const selectedDataCategory of selectedDataCategories) {
      if (
        (!selectedData[selectedDataCategory]) || 
        (categoryData[selectedDataCategory] && categoryData[selectedDataCategory].length === selectedData[selectedDataCategory].length)
      ) {
        mappedData[selectedDataCategory] = "all";
      } else {
        mappedData[selectedDataCategory] = selectedData[selectedDataCategory];
      }
    }
    return mappedData;
  }

  const handleCopyData = async () => {
    const database = schoolInfo.type === "2" ? "elementary" : "edario";
    const passed_data = {
      school_id: schoolInfo.school_id,
      selected_schedule_year_id: selectedScheduleYearID,
      last_subscribed_data_version_id:
        schoolInfo.last_subscribed_data_version_id,
      settings: settings,
      new_year: false,
      database: database,
      selected_data: mapSelectedData(selectedDataCategories, categoryDataSelection),
    };
    setShowCloseModal(false);
    await copyPreviousYearData(passed_data);
    closeModal();
    callBack(passed_data);
  };

  //////////////////////
  ///// USE EFFECT /////
  //////////////////////
  useEffect(() => {
    const constraint_ids = constraints.map((constraint) => constraint.id);
    let temp_settings = null;
    const school_type = schoolInfo.type;

    if (school_type === "2") {
      temp_settings = {
        schedule_version_ids_to_copy: [],
        teachers: selectedDataCategories.includes('teachers') ? true : false,
        courses: selectedDataCategories.includes('courses') ? true : false,
        classrooms: selectedDataCategories.includes('classrooms') ? true : false,
        schedule_structure: true,
        blocks: false,
        teacher_availability: false,
        course_availability: false,
        course_lengths: false,
        homeroom_specials: false,
        course_teachers: false,
        course_classrooms: false,
        teacher_classrooms: false,
      };
    } else {
      temp_settings = {
        schedule_version_ids_to_copy: [],
        schedule_structure: true,
        courses: selectedDataCategories.includes('courses') ? true : false,
        teachers: selectedDataCategories.includes('teachers') ? true : false,
        classrooms: selectedDataCategories.includes('classrooms') ? true : false,
        departments: selectedDataCategories.includes('departments') ? true : false,
        sections: selectedDataCategories.includes('sections') ? true : false,
        users: selectedDataCategories.includes('users') ? true : false,
        course_departments: true,
        teacher_departments: true,
        classroom_departments: true,
        department_supervisors: true,
        section_placements: selectedDataCategories.includes('section_placements') ? true : false,
        section_labels: false,
        // constraints
        teacher_availability: constraint_ids.includes('teacher_availability') ? true : false,
        course_availability: constraint_ids.includes('course_availability') ? true : false,
        course_caps: constraint_ids.includes('course_caps') ? true : false,
        course_lengths: constraint_ids.includes('course_lengths') ? true : false,
        course_flags: constraint_ids.includes('course_flags') ? true : false,
        course_teachers: constraint_ids.includes('course_teachers') ? true : false,
        classroom_settings: constraint_ids.includes('classroom_settings') ? true : false,
        course_classrooms: constraint_ids.includes('course_classrooms') ? true : false,
        teacher_classrooms: constraint_ids.includes('teacher_classrooms') ? true : false,
      };
    }

    // If there is only one schedule version from the selected year, set it as the selected schedule version id
    const schedules_from_current_schedule_year = scheduleInfo.schedules.filter(
      (schedule) =>
        schedule.data_version_id === schoolInfo.current_data_version_id
    );
    if (schedules_from_current_schedule_year.length === 1) {
      const schedule_version_id =
        schedules_from_current_schedule_year[0].schedule_version_id;
      temp_settings.schedule_version_ids_to_copy = [schedule_version_id];

      setSchoolHasOnlyOneScheduleVersionID(true);
    }

    setSettings(temp_settings);
  }, [
    scheduleInfo.schedules,
    schoolInfo.current_data_version_id,
    schoolInfo.type,
    constraints,
    selectedDataCategories
  ]);

  ////////////////////////////////
  ///// INITIALIZE VARIABLES /////
  ////////////////////////////////
  const visibleDataSettings = useMemo(() => {
    return data_settings.filter((data_setting) => !data_setting.hideTab && settings && settings[data_setting.value]);
  }, [data_settings, settings]);

  return (
    <div className="modal-screen">
      <div id="switch-years-modal-content" className="modal-screen-content">
        <div className="switch-years-header">
          <StepsHeader currentScreen={currentScreen} />
        </div>

        {showCloseModal && <span className="data-modal-close" onClick={handleCloseModal}>
          &times;
        </span>}

        <div className="switch-years-page">
          {currentScreen === 1 ? (
            <>
              <h1 id="switch-years-modal-title">Start A New Year</h1>
              <div className="medium-text">
                Congrats, as a repeat user of Edario a lot of your work from
                last year can be reused!
              </div>
              <h4 className="switch-years-subtitle">
                Tell us what data you would like to copy over:
              </h4>

              {settings &&
                data_settings.map((data_setting, i) => {
                  const data_type = data_setting.value;
                  const setting_title = data_setting.display;
                  const explanation_open =
                    constraintExplanationOpen.includes(data_type);

                  let missing_prereqs = [];
                  const setting_prereqs = data_setting.prereqs;
                  const all_prereqs_met = !setting_prereqs
                    ? true
                    : setting_prereqs.reduce((result, prereq) => {
                        if (!settings[prereq]) {
                          result = false;

                          // Find prerequisite display so you can show a list of missing prereqs
                          const setting_index = data_settings.findIndex(
                            (data_setting) => data_setting.value === prereq
                          );
                          const setting_info =
                            setting_index !== -1
                              ? data_settings[setting_index]
                              : null;
                          const setting_display = setting_info
                            ? setting_info.display
                            : null;
                          if (setting_display)
                            missing_prereqs.push(setting_display);
                        }
                        return result;
                      }, true);
                  const missing_prereqs_string =
                    createCommaSeparatedListFromArray(missing_prereqs);

                  return (
                    <React.Fragment key={i}>
                      <div
                        className={`switch-years-schedule-select-container ${
                          !all_prereqs_met &&
                          "switch-years-schedule-select-container-disabled"
                        }`}
                      >
                        <div
                          onClick={
                            all_prereqs_met
                              ? () => toggleSettings(data_type)
                              : null
                          }
                        >
                          {settings[data_type] ? (
                            <FontAwesomeIcon
                              className="switch-years-schedule-select-box fas-checkbox-checked"
                              icon={faCheckSquare}
                            />
                          ) : (
                            <FontAwesomeIcon
                              className="switch-years-schedule-select-box fas-checkbox-unchecked dark-gray-text"
                              icon={faSquare}
                            />
                          )}
                        </div>

                        <div
                          className="capitalize"
                          onClick={
                            all_prereqs_met
                              ? () => toggleSettings(data_type)
                              : null
                          }
                        >
                          {setting_title}
                        </div>
                        {"explanation" in data_setting && (
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            onClick={() =>
                              toggleConstraintExplanation(data_type)
                            }
                          />
                        )}
                      </div>
                      {!all_prereqs_met && (
                        <div className="switch-years-constraint-explanation red-text">
                          This option is not available because you are not
                          rolling over {missing_prereqs_string}.
                        </div>
                      )}
                      {explanation_open && (
                        <div className="switch-years-constraint-explanation">
                          {data_setting.explanation}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              <div className="switch-years-btn-container-fixed">
                <div className="btn-extra-large" style={{ flex: '1 1' }}></div>
                <div
                  className={`btn green-btn btn-extra-large`}
                  onClick={handleMoveToNextPage}
                >
                  Next Step
                </div>
              </div>
            </>
          ) : currentScreen === 2 && selectedDataCategories.length > 0 && currentDataTab ? (
            <>
              <div className="switch-years-data-topics">
                {visibleDataSettings.map((data_setting, index) => {
                  const category = data_setting.value;
                  return (
                    <span
                      className={`${currentDataTab === category ? "active" : ""}`}
                      key={index}
                      onClick={
                        topicDataLoading ? () => {} : () => handleDataTabChange(category)
                      }
                    >
                      {data_setting.display}
                    </span>
                  )
                })}
              </div>
              <h1 id="switch-years-modal-title">
                {
                  visibleDataSettings.find(
                    (data_setting) => data_setting.value === currentDataTab
                  )?.display
                }
              </h1>
              <div className="medium-text">
                By default, all {currentDataTab} are brought over from the
                previous year.
                <br />
                If you’d like to remove certain {currentDataTab} from being
                copied, let us know!
              </div>

              {topicDataLoading && (
                <div
                  className="loader"
                  style={{
                    width: "35px",
                    height: "35px",
                    display: "block",
                    margin: "20px auto",
                  }}
                ></div>
              )}

              {!topicDataLoading && categoryData[currentDataTab] ? (
                <div style={{ width: "80%", margin: "0 auto" }}>
                  <BasicTable
                    key={currentDataTab}
                    headers={mapTopicHeaders(currentDataTab)}
                    rows={mapTopicRows(
                      currentDataTab,
                      categoryData[currentDataTab]
                    )}
                    disabledRowsIds={disabledCategoryDataIds[currentDataTab] || []}
                    checkbox={true}
                    selectedRows={categoryDataSelection[currentDataTab] || null}
                    onSelectionChange={(selectedRows) => {
                      handleTopicDataSelectionChange(
                        currentDataTab,
                        selectedRows
                      );
                    }}
                  />
                </div>
              ) : null}

              {!topicDataLoading && categoryData[currentDataTab] && categoryData[currentDataTab].length === 0 && (
                <div className="alert alert-warning">No sections found with the selected courses.</div>
              )}

              <div className="switch-years-btn-container-fixed">
                <div
                  className={`btn gray-black-btn btn-extra-large ${
                    topicDataLoading ? "disabled" : ""
                  }`}
                  onClick={topicDataLoading ? () => {} : handleMoveToPrevPage}
                >
                  Go Back
                </div>
                <div
                  className={`btn green-btn btn-extra-large ${
                    topicDataLoading ? "disabled" : ""
                  }`}
                  onClick={topicDataLoading ? () => {} : handleMoveToNextPage}
                >
                  Next Step
                </div>
              </div>
            </>
          ) : currentScreen === 3 ? (
            <>
              <h1 id="switch-years-modal-title">Scheduling Constraints</h1>

              <div className="medium-text">
                By default we carry over any scheduling constrants you set last
                year.
                <br />
                If you’d like to stop certain constraints from being copied, let
                us know!
              </div>

              <div style={{ maxWidth: "70%", margin: "2rem auto" }}>
                {constraints.map(
                  (constraint) => (
                    <Collapsible
                      key={constraint.id}
                      {...constraint}
                      isOpen={openConstraint === constraint.id}
                      onToggle={handleConstraintToggle}
                      onCheck={handleCheckConstraint}
                      checked={checkedConstraints.includes(constraint.id)}
                    >
                      <ConstraintsTable
                        key={constraint.id}
                        id={constraint.id}
                        scheduleInfo={scheduleInfo}
                        scheduleStructureData={scheduleStructureData}
                        teachers={teachers}
                        courses={courses}
                        classrooms={classrooms}
                      />
                    </Collapsible>
                  )
                )}
              </div>

              {errorMessage && (
                <div className="error-message-no-box">{errorMessage}</div>
              )}

              <div className="switch-years-btn-container-fixed">
                <div
                  className={`btn gray-black-btn btn-extra-large ${
                    topicDataLoading ? "disabled" : ""
                  }`}
                  onClick={topicDataLoading ? () => {} : handleMoveToPrevPage}
                >
                  Go Back
                </div>
                <div
                  className={`btn green-btn btn-extra-large ${
                    topicDataLoading ? "disabled" : ""
                  }`}
                  onClick={topicDataLoading ? () => {} : handleMoveToNextPage}
                >
                  Next Step
                </div>
              </div>
            </>
          ) : currentScreen === 4 ? (
            <div className="text-align-center">
              <img
                src={require("../images/copying.png")}
                alt="loading..."
                style={{ width: "200px", margin: "50px 0px 25px 0px" }}
              />
              <div style={{ marginBottom: "30px" }}>
                Copying data over to the new year...
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const StepsHeader = ({ currentScreen, setCurrentScreen }) => {
  const steps = [1, 2, 3];
  const headers = ["Select Categories", "Select Data", "Select Constraints"];
  return (
    <div className="steps-wrapper">
      <div className="step-line"></div>
      <div
        className="step-line-active"
        style={{
          width: `${((currentScreen - 1) / (steps.length - 1)) * 100}%`,
        }}
      ></div>
      <div className="step-container">
        {steps.map((step) => (
          <div className="step-wrapper" key={step}>
            <div
              key={step}
              className={`step-item ${currentScreen >= step ? "active" : ""} ${
                setCurrentScreen ? "cursor-clickable" : ""
              }`}
              onClick={() => (setCurrentScreen ? setCurrentScreen(step) : null)}
            >
              {step}
            </div>
            <div className="step-label">{headers[step - 1]}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
